/* eslint-disable max-lines */
import { badgeVariants } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyH4,
  TypographyP2,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import {
  type CreateDocumentPages,
  type CreateTemplatePages,
  type NotePages,
  type SessionPages,
  TemplatePages,
  type TutorialSteps,
} from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { ChevronRight, Maximize2, X } from 'lucide-react';
import * as m from 'motion/react-m';
import Image from 'next/image';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { CompletionPieChart } from '../../completion-pie-chart/completion-pie-chart';
import { MiniCompletionPieChart } from '../../mini-completion-pie-chart/mini-completion-pie-chart';
import TutorialBackground from './background.png';
import CircleCheckIcon from './circle-check-icon';
import GreyCircleIcon from './grey-circle-icon';
import { ReferralSection, type TutorialReferralPorts } from './referral-section';

/** The app's tutorial `Page` union. That type must stay assignable to this one. */
export type TutorialPage =
  | TemplatePages
  | SessionPages
  | CreateTemplatePages
  | NotePages
  | CreateDocumentPages;

export type TutorialStepsMapping = {
  [key in TutorialSteps]?: {
    steps?: ReadonlyArray<{ enum: TutorialPage }>;
  };
};

interface TutorialChecklistState {
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  tutorialStep: { step: TutorialSteps; page: TutorialPage } | undefined;
  setTutorialStep: (
    step: { step: TutorialSteps; page: TutorialPage } | undefined
  ) => void;
  steps: TutorialStepData[];
  nextUncompletedStep: TutorialSteps | undefined;
  recentlyCompletedSteps: readonly TutorialSteps[];
  clearRecentlyCompletedSteps: () => void;
  tutorialStepCurrentPage?: TutorialStepPage;
  onNextPage: () => void;
  onBackPage: () => void;
  hasCompletedAllSteps: boolean;
  completionPercentage: number;
  userName: string;
  showPercentageChart: boolean;
}

export interface TutorialChecklistPorts {
  useTutorial: () => TutorialChecklistState;
  stepsMapping: TutorialStepsMapping;
  useIsTutorialVisible: () => boolean;
  useIsAuthenticated: () => boolean;
  useOnboardingTaskCompletedAt: () => string | null | undefined;
  /** Read in render. The parent must not precompute this boolean. */
  isSuppressed: () => boolean;
  trackPanelOpened: () => void;
  trackPanelClosed: () => void;
  referral: TutorialReferralPorts;
}

interface TutorialStepPage {
  title: string;
  description: string;
  image?: string;
  enum: TutorialPage;
  order: number;
  total: number;
}

interface TutorialStepData {
  title: string;
  completed: boolean;
  open: boolean;
  enum: TutorialSteps;
}

interface TutorialStepItemProps {
  step: TutorialStepData;
  isActive: boolean;
  newlyCompleted: boolean;
  onClick: () => void;
  showDivider: boolean;
}

interface TutorialStepFooterProps {
  onBack: () => void;
  onNext: () => void;
  tutorialStepCurrentPage?: TutorialStepPage;
}

interface TutorialChecklistStepsProps {
  userName: string;
  steps: TutorialStepData[];
  activeTab?: TutorialSteps;
  isNewlyCompletedThisSession: (step: TutorialStepData) => boolean;
  onStepClick: (params: { step: TutorialSteps; page: TutorialPage }) => void;
  stepsMapping: TutorialStepsMapping;
  referral: TutorialReferralPorts;
  onHide: () => void;
  hasCompletedAllSteps: boolean;
  completionPercentage: number;
}

interface TutorialStepViewProps {
  tutorialStepCurrentPage?: TutorialStepPage;
  onBack: () => void;
  onNext: () => void;
}

const TutorialStepItem = memo(
  ({ step, isActive, newlyCompleted, onClick, showDivider }: TutorialStepItemProps) => {
    const [showCheck, setShowCheck] = useState(!newlyCompleted);

    useEffect(() => {
      if (newlyCompleted) {
        const timer = setTimeout(() => {
          setShowCheck(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }, [newlyCompleted]);

    return (
      <m.div
        key={step.enum}
        {...(newlyCompleted && showCheck
          ? {
              initial: { opacity: 0, scale: 0.8 },
              animate: { opacity: 1, scale: 1 },
              transition: { duration: 0.3, delay: 0.5 },
            }
          : {})}
        onClick={onClick}
        className={cn(
          'relative flex cursor-pointer items-center justify-between rounded-md p-2',
          'overflow-visible',
          isActive && 'border border-outline bg-surface shadow-sm hover:cursor-pointer'
        )}
      >
        <div className="flex items-center gap-2">
          <div className="z-10 shrink-0 bg-surface">
            {step.completed ? (
              newlyCompleted ? (
                !showCheck ? (
                  <GreyCircleIcon />
                ) : (
                  <m.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CircleCheckIcon />
                  </m.div>
                )
              ) : (
                <CircleCheckIcon />
              )
            ) : (
              <GreyCircleIcon />
            )}
          </div>

          {isActive ? (
            <TypographyP2Bold
              className={cn(
                'overflow-visible text-text-primary',
                step.completed && 'text-text-primary'
              )}
            >
              {step.title}
            </TypographyP2Bold>
          ) : (
            <TypographyP2
              className={cn(
                'overflow-visible text-text-secondary',
                step.completed && 'text-text-primary'
              )}
            >
              {step.title}
            </TypographyP2>
          )}
        </div>

        {(isActive || step.completed) && (
          <ChevronRight
            className="ms-auto text-text-secondary rtl:-scale-x-100"
            size={16}
          />
        )}

        {showDivider && (
          <div
            className={cn(
              'absolute top-[13px] !ml-[5px] h-full w-[8px]',
              step.completed
                ? 'bg-validation-success'
                : isActive
                  ? '!ml-[4px] mt-[24px] h-2/5 bg-text-disabled'
                  : 'bg-text-disabled'
            )}
            style={{ transformOrigin: 'top' }}
          />
        )}
      </m.div>
    );
  }
);

TutorialStepItem.displayName = 'TutorialStepItem';

const TutorialStepFooter = memo(
  ({ onBack, onNext, tutorialStepCurrentPage }: TutorialStepFooterProps) => {
    const { t } = useTranslations();

    return (
      <div className="mt-1 flex w-full justify-between">
        <div className="flex gap-1 self-center">
          {[...(Array(tutorialStepCurrentPage?.total || 1) as number[])].map(
            (_, index) => (
              <div
                key={index}
                className={cn(
                  'size-2 rounded-full bg-background-secondary',
                  index + 1 === tutorialStepCurrentPage?.order && 'bg-[#0EA5E9]'
                )}
              ></div>
            )
          )}
        </div>
        <div className="flex items-center">
          <button
            className="flex items-center justify-center px-2.5 py-1.5 text-text-secondary"
            onClick={onBack}
          >
            <TypographyCaption>{t('demo.back')}</TypographyCaption>
          </button>

          <button
            className="flex items-center justify-center rounded-md bg-background-secondary px-2.5 py-1.5 text-text-primary hover:bg-background-secondary-hover"
            onClick={onNext}
          >
            <TypographyCaptionBold>{t('demo.next')}</TypographyCaptionBold>
          </button>
        </div>
      </div>
    );
  }
);

TutorialStepFooter.displayName = 'TutorialStepper';

const TutorialChecklistSteps = memo(
  ({
    steps,
    activeTab,
    userName,
    isNewlyCompletedThisSession,
    onStepClick,
    hasCompletedAllSteps,
    completionPercentage,
    stepsMapping,
    referral,
  }: TutorialChecklistStepsProps) => {
    const { t } = useTranslations();

    return (
      <m.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex h-fit w-[318px] max-w-md flex-col overflow-hidden rounded-lg border border-outline bg-surface shadow-md"
      >
        <div className="relative h-[105px] w-full overflow-hidden">
          <Image
            src={TutorialBackground}
            alt="background"
            fill
            className="object-cover"
          />

          <div className="absolute inset-0 z-10 flex flex-row items-center gap-5 p-5">
            <CompletionPieChart
              percentage={completionPercentage}
              className="min-w-fit flex-1"
            />
            <section className="flex flex-col">
              <TypographyH4 className="text-text-primary-invert">
                {hasCompletedAllSteps
                  ? `${t('tutorial.youDidIt')} ${userName}!`
                  : `${t('tutorial.keepItUp')} ${userName}!`}
              </TypographyH4>
              <TypographyCaption className={cn('text-text-primary-invert')}>
                {hasCompletedAllSteps
                  ? t('tutorial.basicsCompleted')
                  : t('tutorial.completeTutorial')}
              </TypographyCaption>
            </section>
          </div>
        </div>

        <div className="p-4">
          {steps.map((step, index) => {
            const isActive = step.enum === activeTab;
            const newlyCompleted = isNewlyCompletedThisSession(step);

            return (
              <TutorialStepItem
                key={step.enum}
                step={step}
                isActive={isActive}
                newlyCompleted={newlyCompleted}
                onClick={() => {
                  if (hasCompletedAllSteps) {
                    // Freed up to review any step
                    const firstPage = stepsMapping[step.enum]?.steps?.[0]?.enum;
                    if (firstPage) onStepClick({ step: step.enum, page: firstPage });
                  } else {
                    if (isActive) {
                      const stepToGo = stepsMapping[step.enum]?.steps?.at(0)?.enum;
                      if (!stepToGo) return;
                      onStepClick({ step: step.enum, page: stepToGo });
                    }
                  }
                }}
                showDivider={index < steps.length - 1}
              />
            );
          })}
          {hasCompletedAllSteps && <ReferralSection referral={referral} />}
        </div>
      </m.div>
    );
  }
);

TutorialChecklistSteps.displayName = 'TutorialChecklistSteps';

const TutorialStepView = memo(
  ({ tutorialStepCurrentPage, onBack, onNext }: TutorialStepViewProps) => {
    return (
      <m.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.3 }}
        className="flex h-fit w-[298px] max-w-md flex-col gap-3 rounded-lg border border-outline bg-surface px-5 py-4 shadow-md"
      >
        <div className="flex flex-col">
          <TypographyPBold>{tutorialStepCurrentPage?.title}</TypographyPBold>
          <TypographyCaption className="text-text-secondary">
            {tutorialStepCurrentPage?.description}
          </TypographyCaption>
        </div>
        <div className="flex flex-col gap-2 overflow-hidden rounded-md border border-outline">
          {tutorialStepCurrentPage?.image ? (
            <video
              src={tutorialStepCurrentPage?.image}
              style={{ height: 'auto', width: '640px' }}
              loop
              muted
              autoPlay
            />
          ) : (
            <div className="flex h-[200px] w-full items-center justify-center bg-text-disabled">
              No image
            </div>
          )}
        </div>
        <TutorialStepFooter
          onBack={onBack}
          onNext={onNext}
          tutorialStepCurrentPage={tutorialStepCurrentPage}
        />
      </m.div>
    );
  }
);

TutorialStepView.displayName = 'TutorialStepView';

export const TutorialChecklist = ({ ports }: { ports: TutorialChecklistPorts }) => {
  const {
    panelOpen,
    setPanelOpen,
    tutorialStep,
    setTutorialStep,
    steps,
    nextUncompletedStep,
    recentlyCompletedSteps,
    clearRecentlyCompletedSteps,
    tutorialStepCurrentPage,
    onNextPage,
    onBackPage,
    hasCompletedAllSteps,
    completionPercentage,
    userName,
    showPercentageChart,
  } = ports.useTutorial();
  const tutorialVisible = ports.useIsTutorialVisible();
  const isAuthenticated = ports.useIsAuthenticated();
  const { t } = useTranslations();
  const onboardingTaskCompletedAt = ports.useOnboardingTaskCompletedAt();

  // Auto-hide tutorials 7 days after completing all steps
  const shouldAutoHide = useMemo(() => {
    if (!onboardingTaskCompletedAt) {
      return false;
    }
    const completedDate = new Date(onboardingTaskCompletedAt);
    const daysSinceCompletion = Math.floor(
      (Date.now() - completedDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysSinceCompletion >= 7;
  }, [onboardingTaskCompletedAt]);

  const handleStepClick = useCallback(
    (step: TutorialSteps, page: TutorialPage) => {
      setTutorialStep({ step, page });
      clearRecentlyCompletedSteps();
    },
    [setTutorialStep, clearRecentlyCompletedSteps]
  );

  const isNewlyCompletedThisSession = useCallback(
    (step: TutorialStepData) => {
      return step.completed && recentlyCompletedSteps.includes(step.enum);
    },
    [recentlyCompletedSteps]
  );

  const activeTab = nextUncompletedStep;

  const currentView = useMemo(() => {
    if (tutorialStep?.step) {
      return (
        <TutorialStepView
          tutorialStepCurrentPage={tutorialStepCurrentPage}
          onBack={onBackPage}
          onNext={onNextPage}
        />
      );
    }
    return (
      <TutorialChecklistSteps
        steps={steps}
        userName={userName}
        activeTab={activeTab}
        isNewlyCompletedThisSession={isNewlyCompletedThisSession}
        hasCompletedAllSteps={hasCompletedAllSteps}
        completionPercentage={completionPercentage}
        stepsMapping={ports.stepsMapping}
        referral={ports.referral}
        onStepClick={(params) => handleStepClick(params.step, params.page)}
        onHide={() => setPanelOpen(false)}
      />
    );
  }, [
    tutorialStep,
    tutorialStepCurrentPage,
    onBackPage,
    onNextPage,
    steps,
    userName,
    activeTab,
    isNewlyCompletedThisSession,
    hasCompletedAllSteps,
    completionPercentage,
    handleStepClick,
    setPanelOpen,
    ports.stepsMapping,
    ports.referral,
  ]);

  // Auth and visibility first. The suppression thunk reads feature flags,
  // and those reads must not run for a signed-out user or a hidden tutorial.
  if (!isAuthenticated || !tutorialVisible || ports.isSuppressed() || shouldAutoHide) {
    return null;
  }

  return (
    <Popover
      open={panelOpen}
      onOpenChange={(open) => {
        if (open) {
          ports.trackPanelOpened();
        } else {
          ports.trackPanelClosed();
        }
        setPanelOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={cn(
            'absolute bottom-3 right-8 z-10 cursor-pointer',
            badgeVariants({
              color: 'neutral',
              size: 'sm',
              className: 'bg-surface',
            })
          )}
        >
          <span> {t('tutorials.tutorials')} </span>{' '}
          {showPercentageChart ? (
            <div className="flex items-center gap-1">
              <span>{completionPercentage}%</span>
              <MiniCompletionPieChart percentage={completionPercentage} />
            </div>
          ) : (
            <Maximize2 size={10} />
          )}
        </m.div>
      </PopoverTrigger>

      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        sideOffset={-26}
        alignOffset={-8}
        side="top"
        align="end"
        className={cn(
          'flex h-fit w-[298px] justify-end overflow-hidden rounded-md border-none p-0',
          tutorialStepCurrentPage?.enum === TemplatePages.SET_AS_DEFAULT_TEMPLATE
            ? '!z-10'
            : '!z-[101]',
          'relative'
        )}
      >
        <Button
          variant="ghost"
          size="xxs"
          iconOnly
          className={cn('absolute end-2 top-2 z-20')}
          onClick={() => setPanelOpen(false)}
          aria-label={t('common.close')}
        >
          <X />
        </Button>

        {currentView}
      </PopoverContent>
    </Popover>
  );
};
